import { useState } from "react";
import { Facebook, Mail, Link2, Check, MessageCircle, MessagesSquare, Smartphone, Twitter, Linkedin, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";

const BTN = "inline-flex items-center justify-center w-10 h-10 rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors duration-300";

export function ShareButtons({ title, label, url: urlProp }) {
  const { t, ts } = useLang();
  const [copied, setCopied] = useState(false);
  const url = urlProp || (typeof window !== "undefined" ? window.location.href : "");
  const enc = encodeURIComponent;
  // Partage natif (mobile : fiche de partage du système — toutes les apps installées)
  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;
  // Le lien sms: ne fait rien sur un ordinateur — proposé seulement sur mobile
  // (UA mobile OU écran tactile, ex. iPad dont l'UA dit « Macintosh »)
  const isMobile = typeof navigator !== "undefined"
    && (/Android|iPhone|iPad|Mobile/i.test(navigator.userAgent) || navigator.maxTouchPoints > 1);

  // Courriel : le mailto: n'ouvre rien si aucun logiciel courriel n'est configuré —
  // on copie donc AUSSI le texte, avec un mot d'aide.
  const emailShare = () => {
    try { navigator.clipboard.writeText(`${title}\n\n${url}`); } catch { /* silencieux */ }
    toast.success(t("blog.share_email_hint"));
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title, text: title, url });
    } catch { /* annulé par l'utilisateur */ }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    toast.success(t("blog.share_copied"));
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-wrap items-center gap-2.5" data-testid="share-buttons">
      {label && <span className="text-sm text-ivory/70 mr-1.5">{label}</span>}
      {canNativeShare && (
        <button type="button" onClick={nativeShare} data-testid="share-native" title={ts("blog.share_native")} className={BTN}>
          <Share2 className="w-4 h-4" />
        </button>
      )}
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`} target="_blank" rel="noreferrer"
        data-testid="share-facebook" title="Facebook" className={BTN}>
        <Facebook className="w-4 h-4" />
      </a>
      <a href="https://m.me/trophee.ca" target="_blank" rel="noreferrer"
        data-testid="share-messenger" title={ts("blog.share_messenger")} className={BTN}>
        <MessagesSquare className="w-4 h-4" />
      </a>
      {isMobile && (
        <a href={`sms:?&body=${enc(title + " " + url)}`}
          data-testid="share-sms" title={ts("blog.share_sms")} className={BTN}>
          <Smartphone className="w-4 h-4" />
        </a>
      )}
      <a href={`https://api.whatsapp.com/send?text=${enc(title + "\n" + url)}`} target="_blank" rel="noreferrer"
        data-testid="share-whatsapp" title="WhatsApp" className={BTN}>
        <MessageCircle className="w-4 h-4" />
      </a>
      <a href={`https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}`} target="_blank" rel="noreferrer"
        data-testid="share-x" title="X (Twitter)" className={BTN}>
        <Twitter className="w-4 h-4" />
      </a>
      <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`} target="_blank" rel="noreferrer"
        data-testid="share-linkedin" title="LinkedIn" className={BTN}>
        <Linkedin className="w-4 h-4" />
      </a>
      <a href={`mailto:?subject=${enc(title)}&body=${enc(title + "\n\n" + url)}`} onClick={emailShare}
        data-testid="share-email" title={ts("blog.share_email")} className={BTN}>
        <Mail className="w-4 h-4" />
      </a>
      <button type="button" onClick={copyLink} data-testid="share-copy" title={ts("blog.share_copy")} className={BTN}>
        {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
      </button>
    </div>
  );
}
